import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  Input,
  effect,
  signal,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ticketPrintableStyles } from '../../../utils'
import 'jsbarcode'
import QRCode from 'qrcode'
import { CoreService, NotificationService } from '../../../services'
import { ProductDetailService } from '../../../product-detail.service'
import { TranslateService } from '@ngx-translate/core'
import { Product, Leaflet } from 'depoto-core/src/entities'
import { FormatPricePipe, ReplacePipe } from '../../../pipes'
import { Router } from '@angular/router'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { DecimalPipe } from '@angular/common'
import { GeoConfig, GeoListItemType } from '../../../configs'
import type { EditorConfig } from 'ckeditor5'

declare const JsBarcode // workaround

type Ckeditor5Module = typeof import('ckeditor5')
type CkeditorEditorConstructor = Ckeditor5Module['ClassicEditor']

type TicketData = {
  productId: 0
  text: ''
  countries: []
}

@Component({
  selector: 'product-labels',
  templateUrl: 'product-labels.component.html',
  styleUrls: ['product-labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductLabelsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('barcode', { static: true }) barcode: ElementRef
  @ViewChild('printable') printableEl: ElementRef
  @ViewChild('printableMulti') printableMultiEl: ElementRef
  @ViewChild('printableMultiToLabelPrinter') printableMultiToLabelPrinterEl: ElementRef
  // @Input() product: Product
  leaflet: Leaflet = new Leaflet()
  // @Input() doRecalculateArray = true
  quantity = 39
  startAt = 0
  // printType: 'single' | 'multi' = 'single' // todo GS1 na archy!!!!!!!!!!!!!!!!!!!!!!!
  // printTypeMultiToLabelPrinter: boolean = false
  // leaflet preview should show TEXT + COUNTRIES by default (no QR)
  codeType: 'qr' | 'barcode' | 'leaflet' = 'leaflet'
  isPrintingSerialNumbers = false
  isGS1 = false
  isBarcodeTitleVisible = true
  isBarcodeEanVisible = true
  isBarcodePriceVisible = true
  isGS1BatchVisible = true
  isGS1ExpirationDateVisible = true
  isBarcodeWide = true
  isCustomData = false
  isCustomDimensions = false
  customTitle: string = ''
  customEan: string = ''
  customPrice: string = ''
  customBatch: string = ''
  customExpirationDate: string = ''
  finalCode: string = ''
  labelTitle: string = ''
  labelEan: string = ''
  labelPrice: string = ''
  // multiTicketData: TicketData[] = []
  multiTicketData: any[] = []
  // singleTicketData: TicketData = {
  //   productId: '',
  //   text: '',
  //   countries: '',
  // }
  singleTicketData: any = {
    productId: '',
    text: '',
    countries: [],
    countriesLine: '',
  }
  barcodeBase64 = ''
  qrBase64 = ''
  ticketArrayWidthMm: number = 210
  ticketWidthMm: number = 80
  // Sensible default for an 80mm-wide label; kept in sync with content while autoHeight is true
  private static readonly DEFAULT_HEIGHT_MM = 15
  ticketHeightMm: number | null = ProductLabelsComponent.DEFAULT_HEIGHT_MM
  // Computed dynamic height (mm) measured from DOM, mirrored into ticketHeightMm while autoHeight is true
  private ticketHeightAutoMm: number | null = ProductLabelsComponent.DEFAULT_HEIGHT_MM
  // If true, height follows content automatically and we also reflect it back to input value.
  // Only a manual edit of the height field (see onHeightChange) turns this off.
  private autoHeight = true
  // Resize observer to continuously adapt to content changes
  private resizeObserver: ResizeObserver | null = null
  // Make preview text comfortably readable by default
  textSizeMultiplier: number = 1.4
  barcodePaddingHorizontalMm = 0
  barcodePaddingVerticalMm = 0
  pageMarginTopMm = 0
  pageMarginLeftMm = 0
  qrHeightMm: number = 0
  // todo check
  fmtPricePipe = new FormatPricePipe(new DecimalPipe('cs-CZ'), new ReplacePipe(), this.core)

  // WYSIWYG editor for the leaflet text
  private ckeditorLoadPromise?: Promise<void>
  ckeditor: CkeditorEditorConstructor | null = null
  ckeditorConfig: EditorConfig | null = null
  ckeditorLoading = true
  ckeditorLoadError = false

  // leaflet
  text = ''
  countries: GeoListItemType[] = []
  selectedCountries = []

  // Fixed base text size (in mm) independent of ticket height)
  private baseTextSizeMm = 6
  // get textFontSizeMm(): number {
  //   return this.baseTextSizeMm * this.textSizeMultiplier
  // }
  get textFontSizeMm(): number {
    return 4
  }

  // Leaflets listing state
  leaflets: Leaflet[] = []
  listLoading = false
  page = 1
  endPage = 1
  sort: 'id' | 'text' = 'id'
  direction: 'ASC' | 'DESC' = 'DESC'
  mode: 'list' | 'create' | 'edit' = 'list'
  editingLeaflet: Leaflet | null = null
  private originalEditingSnapshot: Partial<Leaflet> | null = null

  // Reactive state for TanStack Query keys
  private pageSig = signal(1)
  private sortSig = signal<'id' | 'text'>('id')
  private directionSig = signal<'ASC' | 'DESC'>('DESC')

  // Query: list of leaflets for current product
  readonly queryLeaflets = injectQuery(() => ({
    queryKey: [
      'leaflets',
      {
        product: this.productDetailService.product?.id,
        page: this.pageSig(),
        sort: this.sortSig(),
        direction: this.directionSig(),
      },
    ],
    queryFn: async () => {
      if (!this.productDetailService.product?.id) {
        return { items: [], paginator: { current: 1, endPage: 1 } } as any
      }
      const res = await this.core.services.leaflet.getList(
        {
          page: this.pageSig(),
          sort: this.sortSig(),
          direction: this.directionSig(),
          filters: { product: this.productDetailService.product?.id },
        },
        { id: null, text: null, countries: null, width: null, height: null, product: { id: null } }
      )
      return res
    },
    select: res => {
      return {
        items: (res?.items || []).map((l: any) => new Leaflet(l)),
        paginator: res?.paginator,
      }
    },
  }))

  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private core: CoreService,
    public productDetailService: ProductDetailService,
    private translateService: TranslateService
  ) {
    this.setLocalizedCountries()
    this.translateService.onLangChange.subscribe(() => this.setLocalizedCountries())
    effect(() => {
      const data: any = this.queryLeaflets.data()
      if (data) {
        this.leaflets = data.items || []
        this.endPage = Number(data?.paginator?.endPage || 1)
      }
    })
    // if (this.router.url.startsWith('/product/')) {
    //   this.product = this.productDetailService.product
    //   if (!this.product?.ean || !this.product?.ean?.length) {
    //     this.notificationService.error(this.translateService.instant('barcode-print-array.no-ean'))
    //   }
    // }
  }

  private setLocalizedCountries(): void {
    this.countries = GeoConfig.getCountries(this.translateService.currentLang).map(c => ({ ...c, id: c.code }))
  }

  ngOnInit() {
    this.loadViewSettings()
    this.hydrate()
    // Explicitly fetch list on initial enter to ensure listing API is called
    this.fetchLeaflets(1)
    // Use query to load list initially and keep state in sync
    void this.loadCkeditor()
  }

  private loadCkeditor(): Promise<void> {
    if (this.ckeditorLoadPromise) {
      return this.ckeditorLoadPromise
    }

    this.ckeditorLoading = true
    this.ckeditorLoadError = false
    this.ckeditorLoadPromise = import('ckeditor5')
      .then(ckeditor5 => {
        this.ckeditor = ckeditor5.ClassicEditor
        this.ckeditorConfig = this.createCkeditorConfig(ckeditor5)
      })
      .catch(error => {
        console.warn('Failed to load CKEditor', error)
        this.ckeditorLoadError = true
      })
      .finally(() => {
        this.ckeditorLoading = false
      })

    return this.ckeditorLoadPromise
  }

  private createCkeditorConfig(ckeditor5: Ckeditor5Module): EditorConfig {
    const { Autoformat, Bold, Essentials, Italic, List, Paragraph, TextTransformation } = ckeditor5

    return {
      licenseKey: 'GPL',
      plugins: [Autoformat, Bold, Essentials, Italic, List, Paragraph, TextTransformation],
      toolbar: {
        items: ['undo', 'redo', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList'],
      },
    }
  }

  ngAfterViewInit(): void {
    // Observe size changes of the rendered preview for truly dynamic height
    try {
      const observeTarget = () => {
        // Observe the actual label box, not the decorative .pageCard wrapper
        // (see recalculateDynamicHeight for why that would skew the measurement).
        const targetEl: HTMLElement | null = (this.printableEl?.nativeElement as HTMLElement) || null
        if (!targetEl) return
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            if (!this.autoHeight) return
            this.recalculateDynamicHeight()
          })
          this.resizeObserver.observe(targetEl)
        }
      }
      // Delay to ensure template refs are available
      setTimeout(observeTarget, 0)
    } catch (_) {
      // noop
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    } catch (_) {}
  }

  // Effective height used for layout calculations (mm)
  get effectiveHeightMm(): number {
    const manual = this.ticketHeightMm as any
    if (this.autoHeight || manual == null || manual === '' || isNaN(Number(manual))) {
      return this.ticketHeightAutoMm != null ? this.ticketHeightAutoMm : 20
    }
    return Number(manual)
  }

  // Do not set fixed height at all; let content define it. We will use min-height for manual values.
  get cssHeight(): string | null {
    return null
  }

  // Prefer min-height over fixed height so content can still grow if needed.
  // Apply it in auto mode too: otherwise the box has no height at all until the
  // ResizeObserver measures it, so it renders as a near-zero sliver instead of
  // matching the default (or last measured) value shown in the height field.
  get cssMinHeight(): string | null {
    const mm = this.effectiveHeightMm
    return isFinite(mm) && mm > 0 ? `${mm}mm` : null
  }

  loadViewSettings(): void {
    // Load dedicated Labels tab settings (text, selected countries)
    const labelsSettings = this.core.services.storage.getSync({ key: 'views_product_labels', type: 'Object' })
    if (labelsSettings && Object.keys(labelsSettings).length > 0) {
      this.text = labelsSettings.text || ''
      // Ensure array shape for selected countries
      const arr = Array.isArray(labelsSettings.selectedCountries) ? labelsSettings.selectedCountries : []
      this.selectedCountries = arr
    }

    // Load generic print settings for sizing/paddings if present (backward compatibility)
    const settings = this.core.services.storage.getSync({ key: 'views_print_code', type: 'Object' })
    if (settings && Object.keys(settings).length > 0) {
      this.isBarcodeTitleVisible = settings.isBarcodeTitleVisible || true
      this.isBarcodeEanVisible = settings.isBarcodeEanVisible || true
      this.isBarcodePriceVisible = settings.isBarcodePriceVisible || true
      this.isBarcodeWide = settings.isBarcodeWide || false
      this.isCustomDimensions = settings.isCustomDimensions || false
      this.quantity = Number(settings.quantity) || 39
      // Ensure a reasonable minimum scale for readability
      const loadedTSM = Number(settings.textSizeMultiplier)
      this.textSizeMultiplier = isFinite(loadedTSM) && loadedTSM > 0 ? Math.max(loadedTSM, 1.4) : 1.4
      this.ticketArrayWidthMm = Number(settings.ticketArrayWidthMm) || 210
      this.ticketWidthMm = Number(settings.ticketWidthMm) || 70
      const loadedH = Number(settings.ticketHeightMm)
      if (isFinite(loadedH) && loadedH > 0) {
        this.ticketHeightMm = loadedH
        this.autoHeight = false
      } else {
        this.autoHeight = true
        this.ticketHeightMm = this.ticketHeightAutoMm ?? ProductLabelsComponent.DEFAULT_HEIGHT_MM
      }
      this.barcodePaddingHorizontalMm = Number(settings.barcodePaddingHorizontalMm) || 0
      this.barcodePaddingVerticalMm = Number(settings.barcodePaddingVerticalMm) || 0
      this.pageMarginTopMm = Number(settings.pageMarginTopMm) || 0
      this.pageMarginLeftMm = Number(settings.pageMarginLeftMm) || 0
      this.qrHeightMm = Number(settings.qrHeightMm) || 0
    }
  }

  saveViewSettings(): void {
    const settings = {
      leaflet: this.leaflet,
      text: this.text,
      selectedCountries: this.selectedCountries,
      // Persist current manual height when not auto, otherwise keep 0 to indicate auto
      ticketHeightMm: this.autoHeight ? 0 : this.ticketHeightMm,
    }
    this.core.services.storage.set({ key: 'views_product_labels', type: 'Object' }, settings)
  }

  async hydrate() {
    // this.labelEan = ''
    // this.labelPrice = ''
    // if (window['dataQR']) {
    //   this.isGS1 = true
    //   this.customTitle = window['dataQR'].title || this.productDetailService?.product?.fullName || ''
    //   this.customEan = window['dataQR'].ean
    //   this.customBatch = window['dataQR'].batch
    //   this.customExpirationDate = window['dataQR'].expirationDate
    //   window['dataQR'] = undefined
    // }
    // if (this.isGS1) {
    //   // this.printType = 'single'
    // Keep current code type; do not force QR so leaflet preview can be text-only
    this.isCustomData = true
    this.labelTitle = this.customTitle
    this.labelEan = this.customEan
    this.labelPrice = this.fmtPricePipe.transform(this.customPrice)
    // Initialize/refresh preview data immediately so UI updates without waiting for async QR
    this.singleTicketData = {
      ...(this.singleTicketData || {}),
      productId: this.productDetailService.product?.id,
      text: this.text || '',
      countries: Array.isArray(this.selectedCountries) ? [...this.selectedCountries.map(c => c.name)] : [],
      countriesLine: this.buildCountriesLine(
        Array.isArray(this.selectedCountries) ? this.selectedCountries.map((c: any) => c?.name || c) : []
      ),
      labelTitle: this.labelTitle,
      labelEan: this.labelEan,
      labelPrice: this.labelPrice,
      base64: this.qrBase64 || this.singleTicketData?.base64 || '',
    }
    // Only generate QR when QR preview is active
    if (this.codeType === 'qr') {
      await this.generateGS1QR()
    }
    // After data change, recalc dynamic height if height is not set manually
    this.scheduleRecalculateDynamicHeight()
    // await this.recalculateArray()
    // return
    // }
    // this.singleTicketData = {
    //   productId: this.productDetailService.product.id,
    //   text: this.text,
    //   countries: this.selectedCountries,
    // }
    // if (this.doRecalculateArray) {
    //   this.recalculateArray()
    // }
  }
  //
  // updateStartAt(field: any): void {
  //   // todo remove
  //   this.startAt = field.id
  //   this.recalculateArray()
  // }
  //
  // async recalculateArray(): Promise<void> {
  // }

  onSettingChange(): void {
    // Update preview synchronously (text + countries) for instant feedback
    this.singleTicketData = {
      ...(this.singleTicketData || {}),
      productId: this.productDetailService.product?.id,
      text: this.text || '',
      countries: Array.isArray(this.selectedCountries) ? [...this.selectedCountries.map(c => c.name)] : [],
      countriesLine: this.buildCountriesLine(
        Array.isArray(this.selectedCountries) ? this.selectedCountries.map((c: any) => c?.name || c) : []
      ),
    }
    this.saveViewSettings()
    // Refresh other derived fields (QR/base64 and labels)
    this.hydrate()
  }

  // Called only when the user directly edits the height field. Any other value
  // change (text, countries, width) goes through onSettingChange and must not
  // affect the auto/manual height mode.
  onHeightChange(value: any): void {
    const num = Number(value)
    this.autoHeight = value == null || value === '' || isNaN(num) || num <= 0
    if (!this.autoHeight) {
      this.ticketHeightMm = num
    }
    this.onSettingChange()
  }

  setCountries(countries) {
    this.selectedCountries = countries
    this.onSettingChange()
  }

  // Schedule height recalculation to run after Angular updates the view
  private scheduleRecalculateDynamicHeight(): void {
    if (!this.autoHeight) return
    // Use rAF to measure after DOM paints with new fonts/sizes
    requestAnimationFrame(() => {
      setTimeout(() => this.recalculateDynamicHeight(), 0)
    })
  }

  // Measure the preview content height in px and convert to mm (assuming 96dpi => 1mm ≈ 3.7795px)
  private recalculateDynamicHeight(): void {
    try {
      // Measure the actual label box (.print-label-outline) itself. Do NOT measure
      // .pageCard: it's only a decorative UI wrapper with its own padding/shadow and
      // does not represent the physical label size, which would inflate the result.
      const el: HTMLElement | null = this.printableEl?.nativeElement ?? null
      if (!el) return
      const px = el.scrollHeight || el.clientHeight
      // Convert px to mm (CSS px assumed at 96dpi)
      const mm = (px * 25.4) / 96
      // Guard against zero/NaN
      if (isFinite(mm) && mm > 0) {
        const rounded = Math.round(mm * 100) / 100 // keep 2 decimals
        this.ticketHeightAutoMm = rounded
        // Reflect the content-driven height back into the input so the user sees
        // the actual value. Assigning here (not via the input's modelChange) does
        // not affect autoHeight, so auto mode keeps tracking further changes.
        if (this.autoHeight) {
          this.ticketHeightMm = rounded
        }
      }
    } catch (e) {
      // no-op: keep previous auto height
    }
  }

  // Display helper: return country name if available, else code/id or primitive
  countryLabel(c: any): string {
    if (!c) return ''
    const co: any = c
    if (typeof co === 'object') {
      return co.name || co.code || co.id || ''
    }
    // primitive – try to resolve by code/id against options
    const found = (this.countries || []).find(
      x => (x as any)?.code === co || (x as any)?.id === co || (x as any)?.name === co
    )
    return (found as any)?.name || String(co)
  }

  private buildCountriesLine(names: Array<string | null | undefined>): string {
    const clean = (Array.isArray(names) ? names : [])
      .filter(Boolean)
      .map(n => String(n).trim())
      .filter(n => n.length > 0)
    if (!clean.length) return ''
    const label = this.translateService.instant('product.shippingCountry')
    return `${label}: ${clean.join(', ')}`
  }

  //
  // async generateCode(finalCode: string): Promise<{ barcodeBase64: string; qrBase64: string }> {
  //   JsBarcode(this.barcode.nativeElement, finalCode, {
  //     lineColor: '#000',
  //     width: this.isBarcodeWide ? 2 : 1,
  //     height: 25,
  //     displayValue: false,
  //   })
  //   this.barcodeBase64 = this.barcode.nativeElement.toDataURL()
  //   this.qrBase64 = await QRCode.toDataURL(finalCode.split('(').join('').split(')').join(''))
  //   return { barcodeBase64: this.barcodeBase64, qrBase64: this.qrBase64 }
  // }
  //
  async generateGS1QR() {
    try {
      const padZero = (s: string, len: number) => {
        return s.length >= len ? s : padZero(`0${s}`, len)
      }
      const paddedEan = this.customEan.length === 14 ? this.customEan : padZero(this.customEan, 14)
      const expDate =
        this.customExpirationDate?.length >= 10
          ? `${this.customExpirationDate[2]}${this.customExpirationDate[3]}${this.customExpirationDate[5]}${this.customExpirationDate[6]}${this.customExpirationDate[8]}${this.customExpirationDate[9]}`
          : null
      this.finalCode = `${paddedEan}`
      if (this.customBatch?.length > 0 || this.customExpirationDate?.length > 0) {
        this.finalCode = `(01)${paddedEan}${expDate?.length ? `(17)${expDate}` : ''}${this.customBatch?.length ? `(10)${this.customBatch}` : ''}`
      }
      this.qrBase64 = await QRCode.toDataURL(this.finalCode.split('(').join('').split(')').join(''))
    } catch (err) {
      this.notificationService.error(this.translateService.instant('qrcode.no-ean'))
      console.error(err)
    }
  }
  //
  confirmPreview(): void {
    // todo co ta width a heught tady nema se vzit z inputu
    const pWidth = 900
    const pHeight = 720
    const popupWin = window.open(
      '',
      'Tisk štítků',
      `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${pWidth},height=${pHeight},top=${
        screen.height - (screen.height + pHeight) / 2
      },left=${screen.width - (screen.width + pWidth) / 2}`
    )
    popupWin.document.body.innerHTML = this.printableEl.nativeElement.innerHTML
    const styles = document.createElement('style')
    styles.innerHTML = ticketPrintableStyles(true)
    popupWin.document.head.appendChild(styles)
    setTimeout(() => {
      popupWin.print()
    }, 400)
    setTimeout(() => {
      popupWin.close()
    }, 800)
  }

  // ===== Leaflets CRUD =====
  async fetchLeaflets(page = 1) {
    try {
      this.listLoading = true
      this.page = page
      this.pageSig.set(page)
      const res = await this.core.services.leaflet.getList(
        {
          page: this.page,
          sort: this.sort,
          direction: this.direction,
          filters: { product: this.productDetailService.product?.id },
        },
        {
          id: null,
          text: null,
          countries: null,
          width: null,
          height: null,
          product: { id: null },
        }
      )
      this.leaflets = (res?.items || []).map((l: any) => new Leaflet(l))
      this.endPage = Number(res?.paginator?.endPage || 1)
      // keep query cache consistent
      this.queryLeaflets.refetch()
    } catch (e) {
      console.error(e)
      this.notificationService.error(this.translateService.instant('alert.load-error'))
    } finally {
      this.listLoading = false
    }
  }

  startCreate() {
    this.mode = 'create'
    this.editingLeaflet = null
    this.originalEditingSnapshot = null
    // Defaults: width 80mm, height auto (tracks content, starts at a sensible default)
    this.text = ''
    this.selectedCountries = []
    this.ticketWidthMm = 80
    this.autoHeight = true
    this.ticketHeightMm = this.ticketHeightAutoMm ?? ProductLabelsComponent.DEFAULT_HEIGHT_MM
    this.qrHeightMm = 0
    this.onSettingChange()
  }

  startEdit(leaflet: Leaflet) {
    this.mode = 'edit'
    this.editingLeaflet = leaflet
    this.originalEditingSnapshot = {
      id: leaflet.id,
      text: leaflet.text,
      countries: Array.isArray((leaflet as any).countries) ? [...(leaflet as any).countries] : [],
      width: (leaflet as any).width,
      height: (leaflet as any).height,
    } as any
    this.text = leaflet.text || ''
    this.selectedCountries = ((leaflet as any).countries || []).slice()
    this.ticketWidthMm = Number((leaflet as any).width) || 80
    // When height missing, keep auto/dynamic; otherwise use the stored manual value
    const h = (leaflet as any).height
    this.autoHeight = h == null
    this.ticketHeightMm = h == null ? this.ticketHeightAutoMm ?? ProductLabelsComponent.DEFAULT_HEIGHT_MM : Number(h)
    this.qrHeightMm = Number(h) || 0
    this.onSettingChange()
  }

  async createLeaflet() {
    try {
      this.listLoading = true
      const payload: any = {
        product: this.productDetailService.product?.id,
        text: this.text || undefined,
        countries: this.selectedCountries?.length ? this.selectedCountries : undefined,
        width: this.ticketWidthMm || undefined,
        height: this.ticketHeightMm || undefined,
      }
      const res = await this.core.services.leaflet.create(payload, {
        id: null,
        text: null,
        countries: null,
        width: null,
        height: null,
        product: { id: null, name: null },
      })
      if ((res as any)?.id) {
        this.notificationService.success(this.translateService.instant('alert.leaflet-created'))
        this.mode = 'list'
        this.editingLeaflet = null
        this.originalEditingSnapshot = null
        await this.fetchLeaflets(1)
      }
    } catch (e) {
      console.error(e)
      this.notificationService.error(this.translateService.instant('alert.create-error'))
    } finally {
      this.listLoading = false
    }
  }

  async updateLeaflet() {
    if (!this.editingLeaflet) return
    try {
      this.listLoading = true
      const id = this.editingLeaflet.id
      // send only changed fields
      const changed: any = { id }
      const snap: any = this.originalEditingSnapshot || {}
      if ((this.text || '') !== (snap.text || '')) changed.text = this.text || ''
      const curCountries = this.selectedCountries?.slice()?.sort()?.join(',') || ''
      const snapCountries = (snap.countries || [])?.slice()?.sort()?.join(',') || ''
      if (curCountries !== snapCountries) changed.countries = this.selectedCountries
      const wNow = Number(this.ticketWidthMm) || undefined
      const wOld = Number(snap.width) || undefined
      if (wNow !== wOld) changed.width = wNow
      const hNow = (this.ticketHeightMm as any) == null ? undefined : Number(this.ticketHeightMm)
      const hOld = snap.height == null ? undefined : Number(snap.height)
      if (hNow !== hOld) changed.height = hNow

      const res = await this.core.services.leaflet.update(changed, {
        id: null,
        text: null,
        countries: null,
        width: null,
        height: null,
      })
      if ((res as any)?.id) {
        this.notificationService.success(this.translateService.instant('alert.leaflet-updated'))
        this.mode = 'edit'
        // await this.fetchLeaflets(this.page)
      }
    } catch (e) {
      console.error(e)
      this.notificationService.error(this.translateService.instant('alert.update-error'))
    } finally {
      this.listLoading = false
    }
  }

  async deleteLeaflet(leaflet: Leaflet) {
    if (!leaflet) return
    if (!confirm(this.translateService.instant('delete-leaflet.confirmation'))) return
    try {
      this.listLoading = true
      await this.core.services.leaflet.delete({ id: leaflet.id })
      this.notificationService.success(this.translateService.instant('alert.leaflet-deleted'))
      await this.fetchLeaflets(this.page)
    } catch (e) {
      console.error(e)
      this.notificationService.error(this.translateService.instant('alert.delete-error'))
    } finally {
      this.listLoading = false
    }
  }

  getCountryName(countryCode) {
    const country = this.countries.find(c => c.code === countryCode)
    return country.name
  }

  // Provide ticket data where countries are guaranteed as readable names
  singleTicketDataWithNames() {
    const names = Array.isArray(this.selectedCountries) ? this.selectedCountries.map(c => this.countryLabel(c)) : []
    return {
      ...(this.singleTicketData || {}),
      countries: names,
      countriesLine: this.buildCountriesLine(names),
    }
  }
}
